import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { AlertCircle, AlertTriangle, Loader2, MapPin, Package, RefreshCw } from 'lucide-react';
import { Button } from '../../../../components/ui/Button';
import { OPERATOR_ERROR, isRetryable } from '../../../../features/operator/operatorErrors';
import { SCAN_SOURCE } from '../../../../features/operator/operatorScanGate';
import { fetchStock, lookupCode } from '../../../../features/operator/operatorThunks';
import { slotHasUnlisted } from '../../../../features/operator/operatorUtils';
import { formatPutawayDate } from '../../../../features/putaway/putawayUtils';
import {
  formatQuantity,
  moduleInactiveHint,
  stockStateLabel,
  zoneTypeLabel,
} from '../../../../utils/inventoryLabels';

/** A product's figures, in the order the floor reads them. */
const TOTALS = [
  { key: 'available', label: 'Disponible' },
  { key: 'reserved', label: 'Reservado' },
  { key: 'stored', label: 'Guardado' },
  { key: 'in_receiving', label: 'En recepción' },
  { key: 'picked', label: 'En la mesa' },
  { key: 'other', label: 'Otros' },
];

/** The figure a place adds to, by its `kind`: stored, in_receiving, picked or other. */
const FIGURE_LABELS = Object.fromEntries(TOTALS.map(({ key, label }) => [key, label]));

const amount = (quantity, uom) => `${formatQuantity(quantity)} ${uom || 'un'}`;

const lotLines = ({ lot, expiry_date: expiry }) => [
  lot ? `Lote ${lot}` : null,
  expiry ? `Vence ${formatPutawayDate(expiry)}` : null,
];

// Available is what stock normally is; only another state is worth a word.
const stateLine = (state) => (state && state !== 'available' ? stockStateLabel(state) : null);

const joined = (lines) => lines.filter(Boolean).join(' · ');

const Heading = ({ icon: Icon, title, lines }) => (
  <div className="flex items-start gap-3">
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ninja-gray text-ninja-dark dark:bg-[#2d2d2d] dark:text-white">
      <Icon size={22} />
    </span>
    <div className="min-w-0">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">Stock</p>
      <p className="text-base font-bold text-ninja-dark dark:text-white">{title}</p>
      <p className="text-sm text-text-secondary">{joined(lines)}</p>
    </div>
  </div>
);

/**
 * One place, or one product in a slot, how much there is and, for a place, the
 * figure it adds to. With a `code`, tapping it looks that code up, as scanning
 * it would; a place without one (the packing table) is only read.
 */
const StockRow = ({ testId, data = {}, title, lines, quantity, figure, code, busy, onLookup }) => {
  const body = (
    <>
      <span className="block min-w-0">
        <p className="font-bold text-ninja-dark dark:text-white">{title}</p>
        <p className="text-sm font-normal text-text-secondary">{joined(lines)}</p>
      </span>
      <span className="block shrink-0 text-right">
        <p className="text-base font-bold tabular-nums text-ninja-dark dark:text-white">{quantity}</p>
        {figure && (
          <p data-testid="stock-location-figure" className="text-xs font-semibold text-text-secondary">
            {figure}
          </p>
        )}
      </span>
    </>
  );

  if (!code) {
    return (
      <li data-testid={testId} {...data} className="flex items-start justify-between gap-3 rounded-2xl bg-ninja-gray p-3 dark:bg-[#2d2d2d]">
        {body}
      </li>
    );
  }
  return (
    <li data-testid={testId} {...data}>
      <Button variant="choice" disabled={busy} onClick={() => onLookup(code)} data-testid={`${testId}-lookup`} data-code={code}>
        <span className="flex items-start justify-between gap-3">{body}</span>
      </Button>
    </li>
  );
};

const ListTitle = ({ children }) => <h3 className="text-sm font-semibold text-text-secondary">{children}</h3>;

const TapHint = ({ children }) => <p className="text-xs text-text-secondary">{children}</p>;

const Empty = ({ children }) => (
  <p data-testid="stock-empty" className="rounded-2xl bg-ninja-gray p-3 text-sm text-text-secondary dark:bg-[#2d2d2d]">
    {children}
  </p>
);

const TotalTile = ({ total, label, value, uom }) => {
  const negative = Number(value) < 0;
  return (
    <div
      data-testid="stock-total"
      data-total={total}
      data-negative={negative ? 'true' : undefined}
      className={clsx(
        'rounded-2xl p-3',
        negative
          ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200'
          : 'bg-ninja-gray text-ninja-dark dark:bg-[#2d2d2d] dark:text-white'
      )}
    >
      <dt className={clsx('flex items-center gap-1 text-xs font-semibold', !negative && 'text-text-muted')}>
        {negative && <AlertTriangle size={14} className="shrink-0" />}
        {label}
      </dt>
      <dd className="text-lg font-bold tabular-nums">{amount(value ?? 0, uom)}</dd>
    </div>
  );
};

const ProductStock = ({ data, busy, onLookup }) => {
  const { product } = data;
  const totals = data.totals || {};
  const locations = data.locations || [];

  return (
    <>
      <Heading icon={Package} title={`${product.sku} — ${product.name}`} lines={[product.company_name]} />
      <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {TOTALS.map(({ key, label }) => (
          <TotalTile key={key} total={key} label={label} value={totals[key]} uom={product.base_uom} />
        ))}
      </dl>
      {Number(totals.available) < 0 && (
        <p className="text-sm text-amber-800 dark:text-amber-300">Se prometió más de lo que hay guardado.</p>
      )}
      <ListTitle>Dónde está</ListTitle>
      {locations.some((location) => location.slot_code) && <TapHint>Toca una para consultarla.</TapHint>}
      {locations.length === 0 ? (
        <Empty>Sin stock en ubicaciones.</Empty>
      ) : (
        <ul className="space-y-2">
          {locations.map((location) => (
            <StockRow
              key={`${location.slot_id ?? location.place}|${location.lot}|${location.state}`}
              testId="stock-location"
              data={{ 'data-kind': location.kind }}
              title={location.place || location.slot_code}
              lines={[
                location.slot_code !== location.place ? location.slot_code : null,
                location.zone_type ? zoneTypeLabel(location.zone_type) : null,
                ...lotLines(location),
                stateLine(location.state),
              ]}
              quantity={amount(location.quantity, product.base_uom)}
              figure={FIGURE_LABELS[location.kind]}
              code={location.slot_code}
              busy={busy}
              onLookup={onLookup}
            />
          ))}
        </ul>
      )}
    </>
  );
};

const occupancyLine = ({ capacity, occupancy }) => {
  if (occupancy === null || occupancy === undefined) return null;
  return capacity > 0
    ? `Ocupación ${formatQuantity(occupancy)} de ${formatQuantity(capacity)} un`
    : `Ocupación ${formatQuantity(occupancy)} un, sin límite`;
};

const SlotStock = ({ data, busy, onLookup }) => {
  const { slot } = data;
  const contents = data.contents || [];
  const unlisted = slotHasUnlisted(data);

  return (
    <>
      <Heading
        icon={MapPin}
        title={slot.name || slot.code}
        lines={[slot.code, zoneTypeLabel(slot.zone_type), slot.is_active ? 'Activa' : 'Inactiva', occupancyLine(slot)]}
      />
      <ListTitle>Contenido</ListTitle>
      {contents.length > 0 && <TapHint>Toca uno para consultarlo.</TapHint>}
      {contents.length === 0 ? (
        <Empty>{unlisted ? 'Lo que guarda es de clientes sin el módulo en esta bodega.' : 'La ubicación está vacía.'}</Empty>
      ) : (
        <>
          <ul className="space-y-2">
            {contents.map((item) => (
              <StockRow
                key={`${item.product_id}|${item.company_id}|${item.lot}|${item.state}`}
                testId="stock-content"
                title={`${item.sku} — ${item.name}`}
                lines={[item.company_name, ...lotLines(item), stateLine(item.state)]}
                quantity={amount(item.quantity, item.base_uom)}
                code={item.sku}
                busy={busy}
                onLookup={onLookup}
              />
            ))}
          </ul>
          {unlisted && (
            <p data-testid="stock-unlisted" className="text-sm text-text-secondary">
              Parte de lo que guarda es de clientes sin el módulo en esta bodega.
            </p>
          )}
        </>
      )}
    </>
  );
};

const StockError = ({ error, target }) => {
  const dispatch = useDispatch();
  const hint = error.kind === OPERATOR_ERROR.MODULE_INACTIVE ? moduleInactiveHint(error.reason) : '';

  return (
    <div data-testid="stock-error" data-kind={error.kind} className="space-y-3">
      <div className="flex items-start gap-3">
        <AlertCircle size={20} className="mt-0.5 shrink-0 text-danger" />
        <div className="min-w-0 space-y-1">
          <p className="text-sm font-semibold text-ninja-dark dark:text-white">{error.message}</p>
          {hint && <p className="text-sm text-text-secondary">{hint}</p>}
        </div>
      </div>
      {/* Not a PendingButton: a replaced request may still be in flight. Once sent, the loading takes its place. */}
      {isRetryable(error) && (
        <Button
          variant="outline"
          size="lg"
          fullWidth
          icon={RefreshCw}
          onClick={() => dispatch(fetchStock(target))}
          data-testid="stock-retry"
        >
          Reintentar
        </Button>
      )}
    </div>
  );
};

const PHASE = { LOADING: 'loading', ERROR: 'error', READY: 'ready' };

/**
 * The stock of the Consulta result the operator asked about, right below
 * it: a product's figures and every place it is in, or what a slot holds.
 * Its failures show here and not in the band.
 */
export const StockDetail = () => {
  const dispatch = useDispatch();
  const { target, loading, error, data } = useSelector((state) => state.operator.lookup.stock);
  const looking = useSelector((state) => state.operator.lookup.loading);
  if (!target) return null;

  const lookUp = (code) => dispatch(lookupCode({ code, source: SCAN_SOURCE.TAP }));

  let phase = PHASE.READY;
  if (loading) phase = PHASE.LOADING;
  else if (error) phase = PHASE.ERROR;

  return (
    <section
      data-testid="stock-detail"
      data-kind={data?.kind ?? target.kind}
      data-state={phase}
      aria-busy={loading}
      className="space-y-4 rounded-3xl border border-border bg-white p-4 dark:bg-[#1a1a1a]"
    >
      {phase === PHASE.LOADING && (
        <p data-testid="stock-loading" className="flex items-center justify-center gap-2 py-6 text-sm text-text-secondary">
          <Loader2 size={22} className="animate-spin text-ninja-green" />
          Cargando stock…
        </p>
      )}
      {phase === PHASE.ERROR && <StockError error={error} target={target} />}
      {phase === PHASE.READY && data?.kind === 'product' && <ProductStock data={data} busy={looking} onLookup={lookUp} />}
      {phase === PHASE.READY && data?.kind === 'slot' && <SlotStock data={data} busy={looking} onLookup={lookUp} />}
    </section>
  );
};
